import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useSupplier } from "@/context/SupplierContext";
import { User, Mail, MessageCircle, Building2, MapPin, Handshake, FileText, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";

const CATEGORY_OPTIONS = ["Raw Materials", "Industrial Components", "Electrical", "Mechanical", "IT",
  "Logistics", "Packaging", "Chemicals", "Services", "Construction", "Engineering", "Other"];

export default function SupplierProfile() {
  const { user } = useAuth();
  const { buyers, company } = useSupplier();
  const s = user?.supplier;
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/seller/master-profile").then((r) => {
      const p = r.data.profile;
      setF({
        contact_person: p.contact_person || "", phone: p.phone || "", address: p.address || "",
        annual_revenue_amount: p.annual_revenue?.amount ?? "", annual_revenue_currency: p.annual_revenue?.currency || "INR",
        categories: p.categories || [], msme_registered: !!p.msme_registered, udyam_number: p.udyam_number || "",
        pan: p.pan || "", bank_name: p.bank?.bank_name || "", account_holder_name: p.bank?.account_holder_name || "",
        account_number: "", ifsc_code: p.bank?.ifsc_code || "", branch_name: p.bank?.branch_name || "",
        masked_account: p.bank?.account_number || "",
      });
    }).catch(() => {});
  }, []);

  const set = (k, v) => setF((prev) => ({ ...prev, [k]: v }));
  const toggleCategory = (c) => setF((prev) => ({
    ...prev, categories: prev.categories.includes(c) ? prev.categories.filter((x) => x !== c) : [...prev.categories, c],
  }));

  const saveMaster = async () => {
    setSaving(true);
    try {
      const payload = {
        contact_person: f.contact_person, phone: f.phone, address: f.address,
        annual_revenue_amount: f.annual_revenue_amount === "" ? null : Number(f.annual_revenue_amount),
        annual_revenue_currency: f.annual_revenue_currency,
        categories: f.categories, msme_registered: f.msme_registered,
        udyam_number: f.msme_registered ? f.udyam_number : "",
        pan: f.pan, bank_name: f.bank_name, account_holder_name: f.account_holder_name,
        ifsc_code: f.ifsc_code, branch_name: f.branch_name,
      };
      if (f.account_number.trim()) payload.account_number = f.account_number.trim();
      await api.put("/seller/master-profile", payload);
      toast.success("Supplier information updated");
      set("account_number", "");
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };

  return (
    <div data-testid="supplier-profile" className="mx-auto max-w-2xl space-y-4">
      <h1 className="font-heading text-2xl font-semibold tracking-tight">Profile</h1>
      <div className="rounded-lg border bg-card">
        <div className="flex items-center gap-4 border-b p-5">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-slate-900 text-xl font-semibold text-white">{user?.name?.[0]}</div>
          <div>
            <p className="font-heading text-lg font-semibold">{user?.name}</p>
            <p className="text-sm text-muted-foreground">Seller user · {company || s?.name}</p>
          </div>
        </div>
        <dl className="divide-y px-5">
          {[
            [Building2, "Seller Company", company || s?.name],
            [MapPin, "City", s?.city],
            [User, "Contact Person", s?.contact_person],
            [Mail, "Email", user?.email],
            [MessageCircle, "WhatsApp", s?.phone],
            [FileText, "GST Number", s?.gst_number],
          ].map(([Icon, k, v]) => (
            <div key={k} className="flex items-center gap-3 py-3 text-sm">
              <Icon className="h-4 w-4 text-slate-400" />
              <dt className="w-36 text-muted-foreground">{k}</dt>
              <dd className="font-medium">{v || "—"}</dd>
            </div>
          ))}
        </dl>
      </div>

      <div className="rounded-lg border bg-card" data-testid="supplier-master-form">
        <h2 className="border-b px-5 py-3 font-heading text-base font-semibold">Supplier Information</h2>
        {!f ? (
          <div className="flex justify-center p-8"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
        ) : (
          <div className="space-y-4 p-5">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div><Label>Contact Person</Label><Input data-testid="master-contact-person" className="mt-1.5" value={f.contact_person} onChange={(e) => set("contact_person", e.target.value)} /></div>
              <div><Label>Contact Number</Label><Input data-testid="master-phone" className="mt-1.5" value={f.phone} onChange={(e) => set("phone", e.target.value)} /></div>
            </div>
            <div><Label>Address</Label><Input data-testid="master-address" className="mt-1.5" value={f.address} onChange={(e) => set("address", e.target.value)} /></div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <Label>Annual Revenue</Label>
                <div className="mt-1.5 flex gap-2">
                  <Input data-testid="master-revenue" type="number" min="0" placeholder="Amount" value={f.annual_revenue_amount} onChange={(e) => set("annual_revenue_amount", e.target.value)} />
                  <select data-testid="master-revenue-currency" value={f.annual_revenue_currency} onChange={(e) => set("annual_revenue_currency", e.target.value)}
                    className="w-28 rounded-md border border-input bg-white px-2 text-sm shadow-sm">
                    {["INR", "USD", "EUR"].map((c) => <option key={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <div><Label>PAN Number</Label><Input data-testid="master-pan" className="mt-1.5 font-mono" placeholder="ABCDE1234F" maxLength={10} value={f.pan} onChange={(e) => set("pan", e.target.value.toUpperCase())} /></div>
            </div>
            <div>
              <Label>Categories</Label>
              <div className="mt-1.5 flex flex-wrap gap-2" data-testid="master-categories">
                {CATEGORY_OPTIONS.map((c) => (
                  <label key={c} className={`flex cursor-pointer items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors ${f.categories.includes(c) ? "border-slate-900 bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
                    <Checkbox className="hidden" checked={f.categories.includes(c)} onCheckedChange={() => toggleCategory(c)} />
                    {c}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <Label>MSME Registered?</Label>
              <div className="mt-1.5 flex gap-2">
                {[true, false].map((v) => (
                  <Button key={String(v)} type="button" size="sm" variant={f.msme_registered === v ? "default" : "outline"}
                    data-testid={`master-msme-${v ? "yes" : "no"}`}
                    className={f.msme_registered === v ? "bg-slate-900 hover:bg-slate-800" : ""}
                    onClick={() => set("msme_registered", v)}>{v ? "Yes" : "No"}</Button>
                ))}
              </div>
              {f.msme_registered && (
                <div className="mt-3"><Label>Udyam Registration Number</Label><Input data-testid="master-udyam" className="mt-1.5 font-mono" value={f.udyam_number} onChange={(e) => set("udyam_number", e.target.value)} /></div>
              )}
            </div>
            <div className="rounded-md border p-4">
              <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Bank Account Details</p>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div><Label>Bank Name</Label><Input data-testid="master-bank-name" className="mt-1.5" value={f.bank_name} onChange={(e) => set("bank_name", e.target.value)} /></div>
                <div><Label>Account Holder Name</Label><Input data-testid="master-account-holder" className="mt-1.5" value={f.account_holder_name} onChange={(e) => set("account_holder_name", e.target.value)} /></div>
                <div>
                  <Label>Account Number</Label>
                  <Input data-testid="master-account-number" className="mt-1.5 font-mono" placeholder={f.masked_account ? `${f.masked_account} (re-enter to change)` : "Digits only"}
                    value={f.account_number} onChange={(e) => set("account_number", e.target.value.replace(/\D/g, ""))} />
                </div>
                <div><Label>IFSC Code</Label><Input data-testid="master-ifsc" className="mt-1.5 font-mono" placeholder="HDFC0001234" maxLength={11} value={f.ifsc_code} onChange={(e) => set("ifsc_code", e.target.value.toUpperCase())} /></div>
                <div><Label>Branch Name</Label><Input data-testid="master-branch" className="mt-1.5" value={f.branch_name} onChange={(e) => set("branch_name", e.target.value)} /></div>
              </div>
              <p className="mt-3 text-xs text-muted-foreground">Bank details are stored securely and shown masked (e.g. XXXX XXXX 4821) to authorized roles only.</p>
            </div>
            <Button data-testid="master-save-btn" disabled={saving} onClick={saveMaster} className="bg-slate-900 hover:bg-slate-800">
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save Supplier Information
            </Button>
          </div>
        )}
      </div>

      <div className="rounded-lg border bg-card" data-testid="buyer-relationships">
        <h2 className="flex items-center gap-2 border-b px-5 py-3 font-heading text-sm font-semibold">
          <Handshake className="h-4 w-4" /> Active Buyer Relationships
        </h2>
        <div className="divide-y">
          {buyers.map((b) => (
            <div key={b.org_id} data-testid={`relationship-${b.name.replace(/\W+/g, "-").toLowerCase()}`} className="flex items-center gap-3 px-5 py-3.5">
              <Building2 className="h-4 w-4 shrink-0 text-slate-500" />
              <div className="flex-1">
                <p className="text-sm font-medium">{b.name}</p>
                <p className="text-xs text-muted-foreground">{b.total_pos} total POs</p>
              </div>
              <div className="text-right">
                <p className="font-mono text-sm font-semibold">{b.active_pos} <span className="text-xs font-normal text-muted-foreground">active</span></p>
                <p className={`text-xs font-semibold ${(b.on_time_pct ?? 100) >= 85 ? "text-[#166534]" : "text-[#D97706]"}`}>
                  {b.on_time_pct != null ? `${b.on_time_pct}% on-time` : "no deliveries yet"}
                </p>
              </div>
            </div>
          ))}
        </div>
        <p className="border-t px-5 py-3 text-xs text-muted-foreground">Use the buyer switcher in the top bar to work within a single buyer organization's workspace.</p>
      </div>
    </div>
  );
}
